import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { asyncPoolAll, loadPaginatedFrontend, useFeathers } from '../../app/util';
import { DataDetailToolbar, ModalInner, NamingPatternModal, GeodistrictCreationModal, DistrictsTableWithFilters, InteractiveMap } from '../../components';
import { Add, DataObject as CurlyBraces, MapOutlined, MoreVert } from '@mui/icons-material';
import { Button, CircularProgress, IconButton, Menu, MenuItem, Modal, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { District } from '../../app/feathers/districts/District';
import { Tier2DistrictTypeAddModal } from '../Tier2DistrictTypeAddModal';
import { humanizeDistrictType } from "../../app/util/humanize-district-type";

const Tier2DistrictType = () => {
  const history = useHistory();
  const location = useLocation();
  const { matchName, tier2Type } = useParams<{ matchName: string, tier2Type: string }>();
  const stateCode = matchName.split('-')[0];
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [parentDistrict, setParentDistrict] = useState<District | null>(null);
  const [isAddingDistricts, setIsAddingDistricts] = useState(false);
  const [addingGeodistricts, setAddingGeodistricts] = useState(false);
  const [geoboundaryModalLayout, setGeoboundaryModalLayout] = useState<'default' | 'wide'>('default');
  const [geoboundaryInProgress, setGeoboundaryInProgress] = useState(false);
  const [editingNamingPattern, setEditingNamingPattern] = useState(false);
  const [title, setTitle] = useState('');
  const [mapDistricts, setMapDistricts] = useState<District[]>([]);
  const [mapDistrictsLoading, setMapDistrictsLoading] = useState(false);
  const [districtsRefreshKey, setDistrictsRefreshKey] = useState(0);
  const [mapMenuAnchorEl, setMapMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [deletingMapGeodistricts, setDeletingMapGeodistricts] = useState(false);

  const filterParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      page: searchParams.get('page') || '0',
      sort: searchParams.get('sort') || 'name:asc',
      shapefile: searchParams.get('shapefile'),
      childrenBoundaries: searchParams.get('childrenBoundaries'),
      childrenDistricts: searchParams.get('childrenDistricts'),
      population: searchParams.get('population'),
      search: searchParams.get('search') || '',
    };
  }, [location.search]);
  
  async function loadHeaderData() {
    if (!feathers) {
      return;
    }
    const districtService = feathers.getService('districts');
    const stateConfigService = feathers.getService('state-configurations');
    const [parentDistrict, stateConfig] = await Promise.all([
      districtService.find({ query: { matchName } })
        .then(({ data }) => data?.[0]),
      stateConfigService.get(stateCode),
    ]);
    if (!parentDistrict) {
      return;
    }
    setParentDistrict(parentDistrict);
    setTitle(`${humanizeDistrictType(tier2Type, stateConfig.typeConfigurations)}`);
  }

  useEffect(() => {
    if (!feathers) {
      return;
    }
    loadHeaderData();
  }, [feathers, matchName, tier2Type, stateCode]);

  const loadMapDistricts = useCallback(async () => {
    if (!feathers || !parentDistrict?._id) {
      return;
    }
    setMapDistrictsLoading(true);
    try {
      const districts = await loadPaginatedFrontend<District>(
        'districts',
        {
          parentId: parentDistrict._id,
          type: tier2Type,
          hasGeodistrict: true,
          $includeGeoDistrict: true,
        },
        feathers,
        20,
      );
      setMapDistricts((districts || []).filter(district => Boolean(district.geoDistrict?.boundary)));
    } catch (err) {
      console.error(err);
      setMapDistricts([]);
    } finally {
      setMapDistrictsLoading(false);
    }
  }, [feathers, parentDistrict?._id, tier2Type]);

  useEffect(() => {
    loadMapDistricts();
  }, [loadMapDistricts]);

  useEffect(() => {
    if (!addingGeodistricts || !geoboundaryInProgress) {
      return;
    }
    const message = 'You have geoboundary creation work in progress. Are you sure you want to leave this page?';
    const unblock = history.block(() => message);
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      unblock();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [history, addingGeodistricts, geoboundaryInProgress]);

  const onSetFilterParams = useCallback((updates: Record<string, string | null>) => {
    const searchParams = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
    });
    history.replace({
      pathname: location.pathname,
      search: searchParams.toString() ? `?${searchParams.toString()}` : ''
    });
  }, [history, location.pathname, location.search]);

  const handleDeleteMapGeodistricts = async () => {
    if (!feathers || mapDistricts.length === 0) {
      return;
    }

    const districtIds = mapDistricts.map(district => district._id);
    const confirmed = window.confirm(`Delete geodistricts for ${districtIds.length} districts? District records will be kept.`);
    if (!confirmed) {
      return;
    }

    setDeletingMapGeodistricts(true);
    setMapMenuAnchorEl(null);
    try {
      await asyncPoolAll(5, districtIds, (districtId: string) => feathers.getService('geo-districts').remove(districtId));
      onSetFilterParams({ page: '0' });
      setDistrictsRefreshKey(key => key + 1);
      await loadMapDistricts();
    } catch (err) {
      console.error(err);
      window.alert('Failed to delete geodistricts.');
    } finally {
      setDeletingMapGeodistricts(false);
    }
  };

  return (
    <Wrapper>
      <DataDetailToolbar
        navTree={[
          { text: 'Districts', to: '/states' },
          { text: parentDistrict?.name || '--', to: `/states/${matchName}` },
          { text: title },
        ]}
        onBack={() => history.push(`/states/${matchName}`)}
        actionButtonsComponent={
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Tooltip title="Edit naming patterns">
              <IconButton onClick={() => setEditingNamingPattern(true)}>
                <CurlyBraces/>
              </IconButton>
            </Tooltip>
            <Tooltip title='Add geoboundaries'>
              <IconButton onClick={() => setAddingGeodistricts(true)}>
                <MapOutlined/>
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setIsAddingDistricts(true)}
              endIcon={<Add/>}
            >
              Add
            </Button>
          </div>
        }
      />
      {parentDistrict && (
        <>
          <DistrictMapSection>
            {mapDistricts.length > 0 ? (
              <DistrictMapRow>
                <DistrictMapContainer>
                  <InteractiveMap
                    features={undefined}
                    districts={mapDistricts}
                    sources={mapDistricts.map(district => district.geoDistrict?.source).filter((source): source is string => Boolean(source))}
                  />
                </DistrictMapContainer>
                <Tooltip title="Map actions">
                  <span>
                    <IconButton
                      onClick={(event) => setMapMenuAnchorEl(event.currentTarget)}
                      disabled={deletingMapGeodistricts}
                      style={{ marginTop: 12 }}
                    >
                      {deletingMapGeodistricts ? <CircularProgress size={20} /> : <MoreVert />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Menu
                  anchorEl={mapMenuAnchorEl}
                  open={Boolean(mapMenuAnchorEl)}
                  onClose={() => setMapMenuAnchorEl(null)}
                >
                  <MenuItem
                    onClick={handleDeleteMapGeodistricts}
                    disabled={deletingMapGeodistricts || mapDistricts.length === 0}
                  >
                    Delete geodistrict
                  </MenuItem>
                </Menu>
              </DistrictMapRow>
            ) : (
              !mapDistrictsLoading && (
                <Typography variant="body1" style={{ marginTop: '8px' }}>
                  No geoboundaries are available for this district type yet.
                </Typography>
              )
            )}
          </DistrictMapSection>
          <DistrictsTableWithFilters
            parentId={parentDistrict._id}
            type={tier2Type}
            stateMatchName={matchName}
            filterParams={filterParams}
            disableSearchShortcut={addingGeodistricts}
            onSetFilterParams={onSetFilterParams}
            refreshKey={districtsRefreshKey}
            allowBulkDeleteGeodistrict
            onAfterBulkGeodistrictDelete={() => {
              onSetFilterParams({ page: '0' });
              setDistrictsRefreshKey(key => key + 1);
              loadMapDistricts();
            }}
          />
        </>
      )}
      {isAddingDistricts && parentDistrict && <Tier2DistrictTypeAddModal
        parent={parentDistrict}
        type={tier2Type}
        onClose={() => setIsAddingDistricts(false)}
        onUpdateDistricts={() => {}}
      />}
      <NamingPatternModal
        open={editingNamingPattern && parentDistrict !== null}
        onClose={() => setEditingNamingPattern(false)}
        restrictToTier2Types={[tier2Type]}
        onReloadData={() => {
          onSetFilterParams({ page: '0' });
        }}
        stateId={parentDistrict?._id || ''}
        mode='tier-2-type'
      />
      <Modal
        open={addingGeodistricts}
        onClose={() => {
          if (geoboundaryInProgress && !window.confirm('You have geoboundary creation work in progress. Are you sure you want to close this modal?')) {
            return;
          }
          setAddingGeodistricts(false);
          setGeoboundaryModalLayout('default');
          setGeoboundaryInProgress(false);
        }}
      >
        <ModalInner
          style={geoboundaryModalLayout === 'wide'
            ? { width: '95vw', maxWidth: '95vw', height: '94vh' }
            : { maxWidth: '800px',  }
          }
          title="Add Geoboundaries"
          onClose={() => {
            if (geoboundaryInProgress && !window.confirm('You have geoboundary creation work in progress. Are you sure you want to close this modal?')) {
              return;
            }
            setAddingGeodistricts(false);
            setGeoboundaryModalLayout('default');
            setGeoboundaryInProgress(false);
          }}
        >
          <GeodistrictCreationModal
            state={stateCode}
            parentId={parentDistrict?._id}
            type={tier2Type}
            onModalLayoutChange={setGeoboundaryModalLayout}
            onInProgressChange={setGeoboundaryInProgress}
            onSuccess={() => {
              onSetFilterParams({ page: '0' });
              setDistrictsRefreshKey(key => key + 1);
              loadMapDistricts();
              setAddingGeodistricts(false);
              setGeoboundaryModalLayout('default');
              setGeoboundaryInProgress(false);
            }}
          />
        </ModalInner>
      </Modal>
    </Wrapper>
  );
};


const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

const DistrictMapSection = styled.div`
  margin-top: 28px;
  margin-bottom: 24px;
`;

const DistrictMapRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const DistrictMapContainer = styled.div`
  margin-top: 12px;
  width: 100%;
  max-width: 800px;
  height: 420px;
`;

export default Tier2DistrictType;
